/**
 * The radar's part in the hero (docs/nowcast-plan.md, Stage A). Plain
 * TypeScript; `state/now.svelte.ts` holds the reading and the headers ask
 * this what to say.
 *
 * The hero said "Cloudy" while it poured, because "Cloudy" was a station 9 km
 * away that the rain had not reached yet (plan §1). So the radar decides
 * *whether and how hard it is precipitating*, and the station keeps
 * everything else: temperature, wind, humidity, pressure, its name and its
 * time.
 */

import type { Current, NowReading } from '../api/types'
import { weatherTone } from './tone'

/**
 * How old a frame may be and still say anything. MRMS lands about three
 * minutes behind and the worker asks every two, so a frame older than this is
 * a worker or an upstream in trouble, and a stale radar overrides nothing.
 */
export const RADAR_MAX_AGE_MS = 10 * 60_000

/**
 * The reading, when it should replace the station's words; otherwise null.
 *
 * It adds weather the station has not seen and never erases weather the
 * station measured (owner's decision, plan §8): a wet gauge under a dry radar
 * keeps its "Light Drizzle", because a beam can pass over drizzle near the
 * ground. No coverage, no reading, nothing over the floor, or a frame too old
 * to trust: the station's words stand, as they did before the radar existed.
 */
export function radarOverride(
  current: Pick<Current, 'icon' | 'description'> | null | undefined,
  reading: NowReading | null | undefined,
  nowMs: number,
): NowReading | null {
  if (!current || !reading) return null
  if (!reading.coverage || !reading.precipitating || !reading.label) return null
  const at = Date.parse(reading.time)
  if (!Number.isFinite(at) || nowMs - at > RADAR_MAX_AGE_MS) return null
  if (weatherTone(current) !== 'calm') return null
  return reading
}

/**
 * The observation with the radar's words in it, for the description and the
 * header's tone. Nothing else of the station's changes, and the icon stays:
 * the tone takes the higher of the icon and the text, so "Moderate rain" is
 * rain-blue over a "Cloudy" icon.
 */
export function withRadar<C extends Pick<Current, 'description'>>(current: C | null, radar: NowReading | null): C | null {
  if (!current || !radar?.label) return current
  return { ...current, description: radar.label }
}

/**
 * "Radar 10:46 PM" for the station line, plus the rate for rain. Snow has no
 * number yet: MRMS's rate is liquid-equivalent, and inches of snow need a
 * snow-to-liquid ratio the next stage brings (plan, "Snowfall rate").
 */
export function radarLine(radar: NowReading, timeZone?: string): string {
  const time = new Date(radar.time).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  })
  const rate = radar.type !== 'snow' && radar.rate_in_h != null ? `, ${radar.rate_in_h.toFixed(2)} in/h` : ''
  return `Radar ${time}${rate}`
}
